import { InfiniteData, UseInfiniteQueryResult } from '@tanstack/react-query'
import { FBPageTableContainer } from 'components/FbUI/Page/styles'
import { useInfiniteScroll } from 'hooks/useInfiniteScroll'
import { ReactNode } from 'react'
import { Spinner } from 'react-bootstrap'
import { PiMagnifyingGlass } from 'react-icons/pi'

type Props<TPage, TItem> = {
  infiniteQuery: UseInfiniteQueryResult<InfiniteData<TPage>>
  children: (items: TItem[]) => ReactNode
  extractItems: (page: TPage) => TItem[]
  className?: string
  emptyState?: ReactNode
}

function DefaultEmptyState() {
  return (
    <div className="flex flex-col items-center justify-center p-12 px-6 text-center text-gray-500">
      <div className="text-5xl mb-4 opacity-50">
        <PiMagnifyingGlass />
      </div>
      <div className="text-base font-medium mb-2">No results found</div>
      <div className="text-sm text-gray-400">Try adjusting your filters</div>
    </div>
  )
}

export function MobileInfiniteScrollView<TPage, TItem>({
  infiniteQuery,
  children,
  extractItems,
  className = 'py-4 px-2 overflow-scroll flex flex-col gap-4',
  emptyState,
}: Props<TPage, TItem>) {
  const { data, fetchNextPage, isFetchingNextPage, hasNextPage, isLoading } =
    infiniteQuery
  const items = data?.pages?.flatMap(extractItems)
  const isEmpty = !isLoading && (!items || items.length === 0)
  const { containerRef, sentinelRef } = useInfiniteScroll({
    onLoadMore: fetchNextPage,
    isLoading: isFetchingNextPage,
    hasMore: hasNextPage,
  })
  return (
    <FBPageTableContainer ref={containerRef} className={className}>
      {isEmpty ? (emptyState ?? <DefaultEmptyState />) : children(items || [])}
      {/* Sentinel element for IntersectionObserver - triggers load when visible */}
      <div ref={sentinelRef} style={{ height: '1px' }} />
      <div className="flex items-center justify-center w-full h-full py-4">
        {(isFetchingNextPage || isLoading) && <Spinner animation="border" />}
      </div>
    </FBPageTableContainer>
  )
}
